<template>
    <template>
        <!-- Polylines par branche -->
        <l-polyline v-for="branch in branches" :key="'corr-branch-' + branch.branchId" :lat-lngs="branch.polyline"
            :color="color" :weight="weight" :opacity="opacity" />

        <!-- Cercles de tolérance autour des points -->
        <template v-if="showRadius">
            <l-circle v-for="position in normalizedPositions" :key="'corr-radius-' + position.id"
                :lat-lng="[position.latitude, position.longitude]" :radius="position.radius"
                :color="getRadiusColor(position)" :fillColor="getRadiusColor(position)"
                :fillOpacity="getRadiusFillOpacity(position)" :opacity="getRadiusOpacity(position)"
                :weight="getRadiusWeight(position)" @mouseover="hoveredPositionId = position.id"
                @mouseout="hoveredPositionId = null" @click="$emit('select-position', position)" />
        </template>

        <!-- Geofencings associés au corridoring -->
        <GeofencingLayer v-if="showGeofencings" :geofencings="geofencings" :color="geofencingColor"
            :fill-opacity="geofencingFillOpacity" :show-points="showGeofencingPoints"
            :show-labels="showGeofencingLabels" :labels-permanent="false" />
    </template>
</template>

<script>
import {
    LPolyline,
    LCircle,
} from "@vue-leaflet/vue-leaflet";

import GeofencingLayer from "./GeofencingLayer.vue";

export default {
    name: "CorridoringLayer",
    components: {
        LPolyline,
        LCircle,
        GeofencingLayer,
    },
    props: {
        positions: {
            type: Array,
            default: () => [],
        },
        geofencings: {
            type: Array,
            default: () => [],
        },
        selectedPositionId: {
            type: [Number, String],
            default: null,
        },
        showRadius: {
            type: Boolean,
            default: false,
        },
        showGeofencings: {
            type: Boolean,
            default: true,
        },
        showGeofencingPoints: {
            type: Boolean,
            default: false,
        },
        showGeofencingLabels: {
            type: Boolean,
            default: false,
        },
        color: {
            type: String,
            default: "#0d6efd",
        },
        radiusColor: {
            type: String,
            default: "#0d6efd",
        },
        selectedColor: {
            type: String,
            default: "#6f42c1",
        },
        hoverColor: {
            type: String,
            default: "#0dcaf0",
        },
        geofencingColor: {
            type: String,
            default: "#198754",
        },
        weight: {
            type: Number,
            default: 4,
        },
        opacity: {
            type: Number,
            default: 0.4,
        },
        radiusOpacity: {
            type: Number,
            default: 0.45,
        },
        radiusFillOpacity: {
            type: Number,
            default: 0.08,
        },
        geofencingFillOpacity: {
            type: Number,
            default: 0.14,
        },
        missingRadiusColor: {
            type: String,
            default: "#fd7e14",
        },
    },
    data() {
        return {
            hoveredPositionId: null,
        };
    },
    computed: {
        normalizedPositions() {
            return this.positions
                .map((position, index) => ({
                    ...position,
                    id: position.id ?? index,
                    latitude: Number(position.latitude ?? position.lat),
                    longitude: Number(position.longitude ?? position.lng),
                    branchId: Number(position.branchId ?? 1),
                    seq: Number(position.seq ?? index + 1),
                    radius: Number(position.radius ?? 200),
                }))
                .filter((position) => {
                    return (
                        Number.isFinite(position.latitude) &&
                        Number.isFinite(position.longitude)
                    );
                })
                .sort((a, b) => {
                    if (a.branchId !== b.branchId) {
                        return a.branchId - b.branchId;
                    }

                    return a.seq - b.seq;
                });
        },
        branches() {
            const byBranch = new Map();

            for (const position of this.normalizedPositions) {
                if (!byBranch.has(position.branchId)) {
                    byBranch.set(position.branchId, []);
                }

                byBranch.get(position.branchId).push(position);
            }

            return Array.from(byBranch.entries())
                .map(([branchId, positions]) => ({
                    branchId,
                    polyline: positions.map((position) => [
                        position.latitude,
                        position.longitude,
                    ]),
                }))
                .filter((branch) => branch.polyline.length >= 2);
        },
    },
    methods: {
        isHovered(position) {
            return (
                this.hoveredPositionId !== null &&
                String(position.id) === String(this.hoveredPositionId)
            );
        },
        getRadiusColor(position) {
            if (this.isSelected(position)) {
                return this.selectedColor;
            }

            if (this.isHovered(position)) {
                return this.hoverColor;
            }

            return position.radius ? this.radiusColor : this.missingRadiusColor;
        },
        getRadiusOpacity(position) {
            if (this.hoveredPositionId === null) {
                return 1;
            }

            return this.isHovered(position) || this.isSelected(position) ? 1 : 0.2;
        },
        getRadiusFillOpacity(position) {
            if (this.isSelected(position)) {
                return 0.22;
            }

            if (this.isHovered(position)) {
                return 0.2;
            }

            return this.hoveredPositionId === null ? 0.1 : 0.05;
        },
        getRadiusWeight(position) {
            return this.isHovered(position) || this.isSelected(position) ? 4 : 2;
        },
        isSelected(position) {
            return (
                this.selectedPositionId !== null &&
                String(position.id) === String(this.selectedPositionId)
            );
        },
    },
};
</script>